import { formatDistanceToNow } from 'date-fns';
import { enUS, es, zhCN } from 'date-fns/locale';
// The i18next core, not the app's `@/i18n` singleton: importing that one from a
// formatting utility drags `initReactI18next` into every module that formats a
// date, and any test mocking `react-i18next` then fails to load. The app
// initialises i18next at startup, so `language` is set by the time anything
// renders; before that, the fallback below applies.
import i18n from 'i18next';

export type DateInput = string | number | Date;

// The backend serializes naive UTC datetimes without a timezone marker (e.g.
// "2026-06-22T14:11:48"). `new Date()` would parse those as the browser's LOCAL
// time, skewing relative timestamps by the browser's UTC offset. Treat a
// timezone-less string as UTC; pass through anything already zoned.
const HAS_TZ = /[zZ]$|[+-]\d{2}:?\d{2}$/;

export function parseServerDate(value: DateInput): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  return new Date(HAS_TZ.test(value) ? value : `${value}Z`);
}

const pad = (value: number) => String(value).padStart(2, '0');

/** `YYYY-MM-DD` of a Date in the browser's zone. */
export const toYmd = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Formatters are built once per interface language: they are called in list-row
// renders, and the cache invalidates by itself when the language changes.
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const madridDateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const shortDateFormats = new Map<string, Intl.DateTimeFormat>();
const mediumDateFormats = new Map<string, Intl.DateTimeFormat>();
const mediumDateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const numericDateFormats = new Map<string, Intl.DateTimeFormat>();
const monthYearFormats = new Map<string, Intl.DateTimeFormat>();

function cachedFormat(
  cache: Map<string, Intl.DateTimeFormat>,
  language: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const cached = cache.get(language);
  if (cached) return cached;
  const format = new Intl.DateTimeFormat(language, options);
  cache.set(language, format);
  return format;
}

// date-fns needs a Locale object, not a BCP-47 tag, and the language detector
// can hand us a regional tag (`es-ES`, `zh-CN`), so key on the base subtag.
const currentLanguage = (): string => i18n.language || 'es';

const dateFnsLocales: Record<string, typeof enUS> = { en: enUS, es, zh: zhCN };

function dateFnsLocale(): typeof enUS {
  return dateFnsLocales[currentLanguage().split('-')[0]] ?? enUS;
}

// Scheduled instants (meetings, committees) travel as UTC, so every reader sees
// them in their own zone and in the interface language: omitting `timeZone` is
// exactly "the browser's zone".
export function formatDateTime(value: DateInput): string {
  return cachedFormat(dateTimeFormats, currentLanguage(), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parseServerDate(value));
}

/** Legal/audit instants whose product contract requires Europe/Madrid time. */
export function formatMadridDateTime(value: DateInput): string {
  return cachedFormat(madridDateTimeFormats, currentLanguage(), {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Madrid',
  }).format(parseServerDate(value));
}

/** Day and month only, in the browser's zone and the interface language. */
export function formatShortDate(value: DateInput): string {
  return cachedFormat(shortDateFormats, currentLanguage(), {
    day: 'numeric',
    month: 'short',
  }).format(parseServerDate(value));
}

/** Day, abbreviated month and year — the list-row date shape. */
export function formatMediumDate(value: DateInput): string {
  return cachedFormat(mediumDateFormats, currentLanguage(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parseServerDate(value));
}

/** `formatMediumDate` plus the time of day. */
export function formatMediumDateTime(value: DateInput): string {
  return cachedFormat(mediumDateTimeFormats, currentLanguage(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseServerDate(value));
}

// Matches the default `toLocaleDateString()` shape (4-digit year), rather than
// `dateStyle: 'short'`, which would drop the century.
/** All-numeric calendar date, in the interface language's own field order. */
export function formatNumericDate(value: DateInput): string {
  return cachedFormat(numericDateFormats, currentLanguage(), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(parseServerDate(value));
}

/** Abbreviated month and year, for partial dates such as CV entries. */
export function formatMonthYear(value: DateInput): string {
  return cachedFormat(monthYearFormats, currentLanguage(), {
    month: 'short',
    year: 'numeric',
  }).format(parseServerDate(value));
}

/** Localized distance from now; suffixed ("hace 2 horas") unless opted out. */
export function formatRelativeTime(
  value: DateInput,
  { addSuffix = true }: { addSuffix?: boolean } = {}
): string {
  return formatDistanceToNow(parseServerDate(value), {
    addSuffix,
    locale: dateFnsLocale(),
  });
}

/** UTC instant from the API to the `YYYY-MM-DDTHH:mm` wall clock of the browser. */
export function toLocalWallClock(value: DateInput): string {
  const date = parseServerDate(value);
  return `${toYmd(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `YYYY-MM-DDTHH:mm` wall clock of the browser to the UTC instant the API stores. */
export function fromLocalWallClock(value: string): string {
  return new Date(value).toISOString();
}
